<template>
  <div class="sc-month">
    <div class="sc-month-nav">
      <button
        type="button"
        class="sc-month-arrow"
        :disabled="!canPrev"
        :aria-label="labels.prev"
        @click="$emit('change-month', -1)"
      >
        &lsaquo;
      </button>
      <div class="sc-month-title">{{ monthTitle }}</div>
      <button
        type="button"
        class="sc-month-arrow"
        :disabled="!canNext"
        :aria-label="labels.next"
        @click="$emit('change-month', 1)"
      >
        &rsaquo;
      </button>
    </div>

    <div class="sc-grid sc-grid-head">
      <div v-for="name in weekDays" :key="name" class="sc-weekday">
        {{ name }}
      </div>
    </div>

    <div class="sc-grid">
      <template v-for="(day, index) in cells">
        <div v-if="day === null" :key="'empty-' + index" class="sc-day-empty"></div>
        <button
          v-else
          :key="day.iso"
          type="button"
          class="sc-day"
          :class="{
            'is-disabled': !day.enabled,
            'is-selected': day.iso === selected,
            'is-today': day.iso === today,
          }"
          :disabled="!day.enabled"
          :aria-pressed="day.iso === selected ? 'true' : 'false'"
          :aria-label="day.label"
          @click="$emit('select', day.iso)"
        >
          <span class="sc-day-number">{{ day.number }}</span>
          <span class="sc-day-dots" v-if="day.enabled">
            <span v-if="day.retrait" class="sc-dot sc-dot-retrait"></span>
            <span v-if="day.livraison" class="sc-dot sc-dot-livraison"></span>
          </span>
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import { toIso, fromIso } from "./dates";

export default {
  name: "MonthGrid",
  props: {
    // premier jour du mois affiché, "YYYY-MM-01"
    month: { type: String, required: true },
    locale: { type: String, default: "fr" },
    available: { type: Array, default: () => [] },
    disabledDates: { type: Array, default: () => [] },
    retraitDates: { type: Array, default: () => [] },
    livraisonDates: { type: Array, default: () => [] },
    selected: { type: String, default: null },
    minMonth: { type: String, default: null },
    maxMonth: { type: String, default: null },
    labels: {
      type: Object,
      default: () => ({ prev: "Mois précédent", next: "Mois suivant" }),
    },
  },
  computed: {
    today() {
      return toIso(new Date());
    },
    monthTitle() {
      return new Intl.DateTimeFormat(this.locale, {
        month: "long",
        year: "numeric",
      }).format(fromIso(this.month));
    },
    weekDays() {
      const formatter = new Intl.DateTimeFormat(this.locale, {
        weekday: "short",
      });
      // 2024-01-01 est un lundi
      const names = [];
      for (let i = 0; i < 7; i++) {
        names.push(formatter.format(new Date(2024, 0, 1 + i)));
      }
      return names;
    },
    canPrev() {
      return this.minMonth === null || this.month > this.minMonth;
    },
    canNext() {
      return this.maxMonth === null || this.month < this.maxMonth;
    },
    cells() {
      const first = fromIso(this.month);
      const year = first.getFullYear();
      const month = first.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      // semaine commençant le lundi
      const offset = (first.getDay() + 6) % 7;
      const labelFormatter = new Intl.DateTimeFormat(this.locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
      });

      const cells = [];
      for (let i = 0; i < offset; i++) {
        cells.push(null);
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(year, month, d);
        const iso = toIso(date);
        cells.push({
          iso: iso,
          number: d,
          label: labelFormatter.format(date),
          enabled:
            this.available.indexOf(iso) !== -1 &&
            this.disabledDates.indexOf(iso) === -1,
          retrait: this.retraitDates.indexOf(iso) !== -1,
          livraison: this.livraisonDates.indexOf(iso) !== -1,
        });
      }
      return cells;
    },
  },
};
</script>
